import { EditCardWrapper } from "@/features/Edit/EditCardWrapper.tsx";
import i18n from "@/i18n";
import { getAuthorized } from "@/lib/fetch.tsx";
import { a7FIATimeSerieFilesEdit } from "@/pages/a7/EditForms/a7FIATimeSerieFilesEdit.tsx";
import { A7FIACompositionListMultiEditCard } from "@/pages/a7/MultiEdit/a7_fia_composition_list_multi_edit.tsx";
import { A7FIAIssuerMultiEditCard } from "@/pages/a7/MultiEdit/a7_fia_issuer_multi_edit.tsx";
import { Card, CardHeader, Select, SelectItem } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { A7FIAInstrumentMultiEditCard } from "./a7_fia_instrument_multi_edit";

export function A7FIAMultiEditCardStandalone() {
  const { instrument_id } = useParams<{ instrument_id: string }>();

  if (!instrument_id) {
    return "Instrument introuvable.";
  }

  return <A7FIAMultiEditCard instrument_id={Number(instrument_id)} />;
}

export function A7FIAMultiEditCard({
  instrument_id,
  onBackgroundClick,
}: {
  instrument_id: number;
  onBackgroundClick?: () => void;
}) {
  const [searchParams] = useSearchParams();
  const fund_id = searchParams.get("fund_id") || undefined;

  const [issuer_id, set_issuer_id] = useState<number | undefined>();
  const [instrument_type, set_instrument_type] = useState<string | undefined>();
  const [societe_mere_id, set_societe_mere_id] = useState<number | undefined>();
  const [file_list, set_file_list] = useState<Set<number>>(new Set());

  const { data: dateData } = useQuery({
    queryKey: ["a7_fia_multi_edit", "dates"],
    queryFn: async () => {
      return getAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/a7/fia_multi_edit/dates`,
      ).then((response) => response.json() as Promise<[string]>);
    },
    refetchOnWindowFocus: false,
  });

  const [selectedDate, setSelectedDate] = useState<string | undefined>();

  useEffect(() => {
    const dateFromUrl = searchParams.get("date");
    if (dateFromUrl && dateData?.includes(dateFromUrl)) {
      setSelectedDate(dateFromUrl);
    } else if (dateData && dateData.length > 0) {
      setSelectedDate(dateData[0]);
    }
  }, [dateData, setSelectedDate, searchParams]);

  const backgroundDivRef = useRef<HTMLDivElement>(null);

  if (!instrument_id) {
    return <div>{i18n.t("errors.invalid_id")}</div>;
  }

  return (
    <div
      ref={backgroundDivRef}
      className="h-full gap-2 p-2 flex flex-wrap justify-center overflow-x-hidden relative"
      onClick={(e) => {
        if (!onBackgroundClick || e.target !== backgroundDivRef?.current)
          return;

        onBackgroundClick();
      }}
    >
      <div className="gap-2 flex flex-col">
        <div className="muti-edit:absolute top-2 left-2 z-10">
          <Select
            name="date"
            label="Date"
            className="w-40"
            disallowEmptySelection={true}
            selectedKeys={selectedDate ? [selectedDate] : []}
            onChange={(selected) =>
              setSelectedDate(selected.target.value as string)
            }
          >
            {dateData?.map((date) => (
              <SelectItem key={date} textValue={date}>
                {date}
              </SelectItem>
            )) ?? []}
          </Select>
        </div>
        <div className="w-[40rem]">
          {issuer_id ? (
            <A7FIAIssuerMultiEditCard
              issuer_id={issuer_id}
              selectedDate={selectedDate}
              allowDelete={false}
              cardHeaderText={"Émetteur"}
              issuerMultiFieldOptions={{
                societe_mere_id: { isVisible: false },
              }}
              issuerTsMultiFieldOptions={{
                date: { isVisible: false },
                a7_fia_issuer_id: { isVisible: false },
                cqs_contrepartie: { isVisible: false },
              }}
              set_societe_mere_id={set_societe_mere_id}
            />
          ) : (
            <Card>
              <CardHeader>Pas d'émetteur pour cet instrument.</CardHeader>
            </Card>
          )}
        </div>
        <div className="w-[40rem]">
          {societe_mere_id ? (
            <A7FIAIssuerMultiEditCard
              issuer_id={societe_mere_id}
              selectedDate={selectedDate}
              allowDelete={false}
              issuerMultiFieldOptions={{
                lei: { isVisible: false },
                societe_mere_id: { isVisible: false },
              }}
              issuerTsMultiFieldOptions={{
                default: { isVisible: false },
                notation: { isVisible: false },
                secteur_nace: { isVisible: true },
                code_iso_du_pays: { isVisible: true },
              }}
              cardHeaderText={"Société mère"}
            />
          ) : (
            <Card>
              <CardHeader>Pas de société mère pour cet émetteur.</CardHeader>
            </Card>
          )}
        </div>
      </div>
      <div className="gap-2 flex flex-col">
        <A7FIACompositionListMultiEditCard
          set_file_list={set_file_list}
          instrument_id={Number(instrument_id)}
          selectedDate={selectedDate}
          instrument_type={instrument_type}
          fund_id={fund_id}
        />
        <A7FIAInstrumentMultiEditCard
          instrument_id={Number(instrument_id)}
          selectedDate={selectedDate}
          set_issuer_id={set_issuer_id}
          set_instrument_type={set_instrument_type}
        />
      </div>
      {[...file_list].map((f) => (
        <div className="w-full max-w-[67rem]">
          <EditCardWrapper
            client={"a7"}
            headerText={"Commentaire"}
            allowDelete={false}
            id={Number(f)}
            {...a7FIATimeSerieFilesEdit}
            multiFieldOptions={{
              default: { isVisible: false },
              status: { isVisible: true },
              comment: { isVisible: true },
            }}
          />
        </div>
      ))}
    </div>
  );
}
